import type { ProcessedProductFamilyPreview } from '@app/data/clients/rbServerSideClient/queries/GetProductFamily/getProductFamilyProcessor';
import { defineMessages, IntlShape } from '@redbubble/next/intl';
import nextSeoMetaTags from '@redbubble/next-seo/meta';
import { MetaTag, OpenGraph, OpenGraphMedia } from '@redbubble/next-seo/types';
import {
  AlternateProductPageUrl,
  CategorySet,
  DefaultInventoryItem,
  InventoryItem,
} from 'types/shop-bff/types';
import { AVAILABLE_PRODUCTS_ORDERING, CANONICAL_GROUPS } from './constants';
import type {
  AdditionalOpenGraph,
  LanguageAlternative,
  Meta,
  OpenGraphProps,
  Preview,
} from './types';

export const getOrderedAvailableProducts = (
  inventoryItems: DefaultInventoryItem[]
) => {
  const sortedItems = inventoryItems
    .sort((a, b) => {
      if (a && b) {
        return (
          AVAILABLE_PRODUCTS_ORDERING.indexOf(a.productTypeId) -
          AVAILABLE_PRODUCTS_ORDERING.indexOf(b.productTypeId)
        );
      }
      return 0;
    })
    .filter(
      (product) =>
        product && AVAILABLE_PRODUCTS_ORDERING.includes(product.productTypeId)
    );

  return sortedItems;
};

/**
 * 1. Finds which of the CANONICAL_GROUPS the inventoryItem.productTypeId passed in belongs to
 * 2. Sets canonicalProductTypeId as the first element of that group as the canonicalTypeId
 * 3. Finally tries to return the available product that has a productTypeId that matches the found
 *    canonicalProductTypeId, otherwise returns null as
 */
export const getCanonicalInventoryItem = (
  inventoryItem: InventoryItem,
  availableProducts: DefaultInventoryItem[]
) => {
  if (
    !inventoryItem ||
    !Array.isArray(availableProducts) ||
    availableProducts.length === 0
  ) {
    return null;
  }

  const { productTypeId } = inventoryItem;

  const canonicalProductTypeId = Object.values(CANONICAL_GROUPS).reduce(
    (memo, preferredProductTypeIds) => {
      if (preferredProductTypeIds.includes(productTypeId)) {
        const intersectedProductTypeId = preferredProductTypeIds.find((ptId) =>
          availableProducts.some((ap) => ptId === ap?.productTypeId)
        );
        return intersectedProductTypeId || memo;
      }
      return memo;
    },
    productTypeId
  );

  return (
    availableProducts.find(
      (ap) => ap?.productTypeId === canonicalProductTypeId
    ) || null
  );
};

const PRODUCT_CLOSE_PREVIEW = 'product_close';
const SEO_TITLE_SUFFIX = {
  en: ' for Sale',
};
const seoForSale = (locale: string | null | undefined) => {
  return locale && SEO_TITLE_SUFFIX[locale] ? SEO_TITLE_SUFFIX[locale] : '';
};

const messages = defineMessages({
  metaTitle: {
    defaultMessage: '"{title}" {product} by {artist}',
  },
  metaOgDescription: {
    defaultMessage:
      '{description} • Millions of unique designs by independent artists. Find your thing.',
  },
  metaProductDescription: {
    defaultMessage: 'Buy "{title}" by {artist} as a {product}',
  },
  metaDescription: {
    defaultMessage: '"{title}" by {artist}',
  },
});

export const getProductTitle = (
  title: string,
  product: string,
  artist: string,
  intlFormatter: IntlShape,
  locale: string
): string => {
  const productForSale = product + seoForSale(locale);
  return intlFormatter.formatMessage(messages.metaTitle, {
    title,
    product: productForSale,
    artist,
  });
};

export const getProductDescription = (
  description: string,
  intlFormatter: IntlShape
): string => {
  return intlFormatter.formatMessage(messages.metaOgDescription, {
    description,
  });
};

const getPageDescription = (
  work: CategorySet['defaultItem']['work'],
  description: string,
  intlFormatter: IntlShape
): string => {
  if (!work) return '';

  const title = work.title ?? '';
  const desc = work.description ?? '';
  const artist = work.artist?.shopDisplayName ?? '';

  let result;

  if (description) {
    result = intlFormatter.formatMessage(messages.metaProductDescription, {
      title,
      artist,
      product: description,
    });
  } else {
    result = intlFormatter.formatMessage(messages.metaDescription, {
      title,
      artist,
    });
  }

  if (result.length < 60) {
    result = `${result}. ${desc || ''}`;
  }

  return result.substr(0, 242);
};

const getPreviewURL = (previewTypeId: string, previews?: Preview[]) => {
  if (!Array.isArray(previews)) return null;

  const filteredPreview = previews.find(
    (preview) => preview.previewTypeId === previewTypeId
  );

  return filteredPreview?.url ?? '';
};

export const additionalOpenGraphTags = ({
  price,
}: OpenGraphProps): AdditionalOpenGraph => {
  return {
    prefix:
      'og: http://ogp.me/ns# profile: http://ogp.me/ns/profile# product: http://ogp.me/ns/product#',
    type: 'product',
    product: { price },
  };
};

export const formatMetaData = (
  meta: Meta,
  namespace: string,
  delim = ':'
): any[] => {
  if (!meta) return [];

  return Object.keys(meta)
    .filter((key) => !key.startsWith('__'))
    .filter((key) => key !== 'prefix')
    .reduce<any[]>((memo, key) => {
      const value: any = meta[key];
      const newKey: string = `${namespace}${delim}${key}`;
      let newMemo = memo;

      if (value) {
        if (typeof value === 'object') {
          newMemo = newMemo.concat(formatMetaData(value, newKey, delim));
        } else {
          newMemo.push([newKey, value]);
        }
      }

      return newMemo;
    }, []);
};

export const twitterUrl = (
  externalSites: Array<{ site: string; url: string }> | undefined
) => {
  if (externalSites) {
    return externalSites
      .reduce((url, externalSite) => {
        const { site } = externalSite;

        return site === 'twitter' ? externalSite.url : url;
      }, '')
      .split('/')
      .pop();
  }
  return '';
};

const getPreviewImage = (
  tag: string,
  previews?: ProcessedProductFamilyPreview[]
) => {
  const previewImage = previews?.find(
    (preview) => preview.previewTypeId === tag
  );

  return previewImage ? previewImage.url : '';
};

const getProductPageUrls = (canonicalItem: DefaultInventoryItem) => {
  return (
    canonicalItem?.productPageUrls?.url ??
    canonicalItem?.productPageUrls?.fallbackUrl
  );
};

export const getCanonicalUrl = (
  currentInventoryItem: InventoryItem,
  productPageUrl: string,
  availableProducts?: DefaultInventoryItem[] | null
) => {
  if (Array.isArray(availableProducts) && availableProducts.length > 0) {
    const products = getOrderedAvailableProducts(availableProducts);

    const canonicalItem = getCanonicalInventoryItem(
      currentInventoryItem,
      products
    );

    const canonicalUrl = getProductPageUrls(canonicalItem);
    if (canonicalUrl) {
      return canonicalUrl;
    }
  }
  return productPageUrl;
};

const getCanonicalId = (
  canonicalUrl: string,
  artistUsername: string
): string | undefined => {
  // Accounting for URLs with groupIds
  const urlSectionMatcher = new RegExp(`${artistUsername}/[0-9]+.[A-Z0-9]{5}`);
  const urlSection = canonicalUrl.match(urlSectionMatcher)?.[0].split('.')?.[1];
  return urlSection;
};

export const getAlternateProductPageLinks = (
  alternateProductUrls: AlternateProductPageUrl[],
  itemId: string,
  canonicalUrl: string,
  artistUsername: string
): LanguageAlternative[] | undefined => {
  const isCanonicalUrl =
    itemId === getCanonicalId(canonicalUrl, artistUsername);
  if (isCanonicalUrl) {
    const alts = alternateProductUrls.reduce<LanguageAlternative[] | undefined>(
      (acc, alt) => {
        if (!alt) return acc;
        const { url, locale } = alt;
        if (acc && url && locale) {
          acc.push({ hrefLang: locale, href: url });
        }
        return acc;
      },
      []
    );

    if (alts && alts.length > 0) {
      return alts;
    }
  }
  return undefined;
};

export const getMetaTags = (
  inventoryCategorySet: CategorySet,
  currentInventoryItem: InventoryItem,
  intlFormatter: IntlShape,
  longDescription: string[],
  availableProducts?: DefaultInventoryItem[] | null,
  alternateProductUrls?: AlternateProductPageUrl[] | null,
  productTypeUrlIdBasedRequest?: boolean
) => {
  const { defaultItem } = inventoryCategorySet;
  const { work, description } = defaultItem;
  const { price, productPageUrl, previewSet, stockStatus } =
    currentInventoryItem;
  const artistName = work.artist?.shopDisplayName ?? '';
  const artistUsername = work.artist?.username ?? '';
  const originalLocale = work.originalLocale ?? 'en';
  const defaultItemProductPageUrl = defaultItem.productPageUrl ?? '';
  const productPreviewUrl =
    getPreviewURL(PRODUCT_CLOSE_PREVIEW, previewSet?.previews) ?? '';
  const title = work.title ?? '';
  const noindex = !work.productPageIndexable;
  const productTitle = getProductTitle(
    title,
    description,
    artistName,
    intlFormatter,
    originalLocale
  );

  const titleTemplate = '%s | Redbubble';

  const desc = getPageDescription(work, description, intlFormatter);

  // if we are using a ptuid url there is only one canonical url possible
  // that is the defaultItem productPageUrl
  const canonicalUrl = productTypeUrlIdBasedRequest
    ? defaultItemProductPageUrl
    : getCanonicalUrl(
        currentInventoryItem,
        defaultItemProductPageUrl,
        availableProducts
      );
  // Defaulting to true to be safe in case a mature work isn't correctly marked.
  // Choice being we don't want to accidently show mature content when it isn't appropriate.
  const hasMatureContent = work.matureContent ?? true;

  const defaultMetaTags: ReadonlyArray<MetaTag> = [
    {
      name: 'keywords',
      content: work.tags ? work.tags.join(', ') : '',
    },
  ];

  const additionalOGTags = additionalOpenGraphTags({
    price,
    productPageUrl,
  });

  const ogMetaTags = [
    ['og', additionalOGTags, 'property'],
    ['product', additionalOGTags.product, 'property'],
  ]
    .flatMap(([namespace, data, metaKey, delimeter]: any) =>
      formatMetaData(data, namespace, delimeter).map(([name, content]) => ({
        name,
        content,
        metaKey,
      }))
    )
    .map(({ name, content, metaKey }: any) => {
      const newMetaTag: any = {};

      newMetaTag.name = name;
      newMetaTag.content = content;

      if (metaKey === 'property') {
        newMetaTag[metaKey] = name;
        newMetaTag.name = undefined;
      }

      return newMetaTag as MetaTag;
    });

  const additionalMetaTags = [
    ...defaultMetaTags,
    ...nextSeoMetaTags,
    ...ogMetaTags,
  ];

  const openGraphImages: OpenGraphMedia[] = productPreviewUrl
    ? [{ url: productPreviewUrl }]
    : [];
  const jsonLdImages: string[] = productPreviewUrl ? [productPreviewUrl] : [];

  const twitter = twitterUrl(work.artist?.externalSites);

  const productDescription = getProductDescription(
    `${work.description}`,
    intlFormatter
  );

  const longProductDescription = longDescription.join('. ');

  const openGraph: OpenGraph = {
    description: work.description ? productDescription : '',
    images: openGraphImages,
    locale: originalLocale,
    url: productPageUrl || defaultItemProductPageUrl,
  };

  const preview = getPreviewImage('primary_square', previewSet?.previews);

  const languageAlternates =
    alternateProductUrls && !noindex
      ? getAlternateProductPageLinks(
          alternateProductUrls,
          currentInventoryItem.blankItemId,
          canonicalUrl,
          artistUsername
        )
      : undefined;

  return {
    productTitle,
    titleTemplate,
    title,
    desc,
    ...(noindex ? {} : { canonicalUrl }),
    productPageUrl,
    hasMatureContent,
    additionalMetaTags,
    openGraph,
    twitter,
    preview,
    price,
    jsonLdImages,
    languageAlternates,
    noindex,
    longProductDescription,
    description,
    stockStatus: stockStatus.status,
  };
};
